import {
  REGISTRY_BLOCK,
  parseUtilityDismissible,
  parseUtilityRotateMs,
  type LayoutBlock,
} from '@web-studio/shared-schemas';
import {
  LUCIDE_SSR_CHEVRON_RIGHT,
  LUCIDE_SSR_MAIL,
  LUCIDE_SSR_MAP_PIN,
  LUCIDE_SSR_PHONE,
  LUCIDE_SSR_X,
  resolveSectionSsrIcon,
} from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml, renderSocialIconHtml } from '../icons/renderIconHtml';
import { pickBlockField } from '../lib/localized';
import { resolveUtilityBarItems } from '../lib/resolveUtilityBarItems';
import { pickChrome } from '../lib/siteChromeCopy';
import { escapeHtml } from './escape';

function resolveArrangement(registryBlockKey?: string): string {
  if (registryBlockKey === REGISTRY_BLOCK.presentation.utilityBarStatic) return 'static';
  if (registryBlockKey === REGISTRY_BLOCK.presentation.utilityBarCarousel) return 'carousel';
  if (registryBlockKey === REGISTRY_BLOCK.presentation.utilityBarCollapse) return 'collapse';
  return 'sticky';
}

/** Phone chrome: icon wins; else label; else omit (mirror UtilityBar.tsx). */
function resolveUtilityFace(item: ReturnType<typeof resolveUtilityBarItems>[number]): 'icon' | 'label' | null {
  if (item.kind === 'social') return 'icon';
  if (item.icon?.trim()) return 'icon';
  if (item.label?.trim()) return 'label';
  return null;
}

export function renderUtilityBarHtml(block: LayoutBlock, locale: string): string {
  if (block.hidden === true) {
    return '';
  }
  const items = resolveUtilityBarItems(block, locale);
  const faced = items
    .map((item) => {
      const face = resolveUtilityFace(item);
      return face ? { item, face } : null;
    })
    .filter((row): row is { item: (typeof items)[number]; face: 'icon' | 'label' } => row != null);
  if (faced.length === 0) {
    return '';
  }

  const arrangement = resolveArrangement(block.registryBlockKey);
  const dismissible = parseUtilityDismissible(
    pickBlockField(block.content, 'dismissible', locale),
  );
  const rotateMs = parseUtilityRotateMs(pickBlockField(block.content, 'rotateMs', locale));
  const regionLabel = pickChrome('utilityBarRegion', locale);
  const dismissLabel = pickChrome('utilityBarDismiss', locale);
  const expandLabel = pickChrome('utilityBarExpand', locale);
  const collapseLabel = pickChrome('utilityBarCollapse', locale);
  const panelId = `ws-utility-${escapeHtml(block.blockId.slice(0, 8))}`;
  const chevron = LUCIDE_SSR_CHEVRON_RIGHT;
  const mailIcon = LUCIDE_SSR_MAIL;
  const mapIcon = LUCIDE_SSR_MAP_PIN;

  const listHtml = faced
    .map(({ item, face }, index) => {
      const hiddenAttr =
        arrangement === 'carousel' && index > 0 ? ' hidden' : '';
      const faceClass =
        face === 'icon' ? 'ws-utility__item--icon' : 'ws-utility__item--label-only';
      if (item.kind === 'social') {
        const icon = renderSocialIconHtml(item.network, {
          size: 'sm',
          className: 'ws-utility__icon',
        });
        return `<li data-ws-utility-slide="${escapeHtml(item.id)}"${hiddenAttr}><a class="ws-utility__item ws-utility__item--social ${faceClass}" href="${escapeHtml(item.href)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(item.label)}" data-utility-kind="social" data-utility-face="${face}">${icon}<span class="ws-utility__text">${escapeHtml(item.label)}</span></a></li>`;
      }
      const fallback =
        item.kind === 'email' ? mailIcon : item.kind === 'address' ? mapIcon : LUCIDE_SSR_PHONE;
      const icon = resolveSectionSsrIcon(item.icon, fallback);
      const iconHtml =
        face === 'icon'
          ? `<span class="ws-utility__icon-hit">${renderLucideIconHtml(icon.iconNode, {
              size: 'sm',
              className: 'ws-utility__icon',
              name: icon.name,
            })}</span>`
          : '';
      const body = `${iconHtml}<span class="ws-utility__text">${escapeHtml(item.label)}</span>`;
      if (item.kind === 'address') {
        return `<li data-ws-utility-slide="${escapeHtml(item.id)}"${hiddenAttr}><span class="ws-utility__item ${faceClass}" data-utility-kind="address" data-utility-face="${face}">${body}</span></li>`;
      }
      return `<li data-ws-utility-slide="${escapeHtml(item.id)}"${hiddenAttr}><a class="ws-utility__item ${faceClass}" href="${escapeHtml(item.href)}" aria-label="${escapeHtml(item.label)}" data-utility-kind="${item.kind}" data-utility-face="${face}">${body}</a></li>`;
    })
    .join('');

  const teaser =
    arrangement === 'collapse'
      ? `<button type="button" class="ws-utility__teaser" aria-expanded="false" aria-controls="${panelId}" aria-label="${escapeHtml(expandLabel)}" data-ws-utility-teaser data-ws-utility-expand="${escapeHtml(expandLabel)}" data-ws-utility-collapse="${escapeHtml(collapseLabel)}">${renderLucideIconHtml(chevron.iconNode, { size: 'sm', className: 'ws-utility__teaser-icon', name: chevron.name })}</button>`
      : '';

  const dismiss = dismissible
    ? `<button type="button" class="ws-utility__dismiss" aria-label="${escapeHtml(dismissLabel)}" data-ws-utility-dismiss>${renderLucideIconHtml(LUCIDE_SSR_X.iconNode, { size: 'sm', name: LUCIDE_SSR_X.name })}</button>`
    : '';

  const collapseClass = arrangement === 'collapse' ? ' ws-utility--collapsed' : '';

  return `<div class="ws-utility ws-utility--${arrangement}${collapseClass}" data-block-id="${escapeHtml(block.blockId)}" data-component="Utility_Bar" data-utility-arrangement="${arrangement}" data-utility-dismissible="${dismissible ? 'true' : 'false'}" data-utility-rotate-ms="${rotateMs}" data-ws-utility role="region" aria-label="${escapeHtml(regionLabel)}">${teaser}<div class="ws-utility__inner" id="${panelId}"${arrangement === 'collapse' ? ' hidden' : ''}><ul class="ws-utility__list" data-ws-utility-list>${listHtml}</ul>${dismiss}</div></div>`;
}
